import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { useParamsSearch } from '@anytag/shared-hooks';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell, TableV2 } from '@anytag/shared-atoms';
import { getOffset, formatPrice, formatPercent, formatIntNumber, getValuableVariables } from '@anytag/shared-utils';
import {
  MarketplaceAffiliateReportByDate,
  MarketplaceAffiliateReportSummary,
  MarketplaceAffiliateStatsItem,
  useMarketplaceAffiliateReportByDateQuery
} from '@anytag/graphql';
import { MarketplaceAffiliateReportSearchSchemaType } from '../MarketplaceAffiliateReportFilter';
import { formatAffiliatePeriod } from './utils';

export interface MarketplaceAffiliateReportByDateRowData {
  className?: string;
  stats?: MarketplaceAffiliateStatsItem;
  date?: MarketplaceAffiliateReportByDate['date'];
}

interface MarketplaceAffiliateReportByDateTableProps {
  currency: string;
  campaignId: number;
  isReferralCampaign?: boolean;
  summaryData?: MarketplaceAffiliateReportSummary;
}

export const MarketplaceAffiliateReportByDateTable = ({
  currency,
  campaignId,
  summaryData,
  isReferralCampaign
}: MarketplaceAffiliateReportByDateTableProps) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const {
    page,
    limit,
    filter: { startDate, endDate }
  } = useParamsSearch<MarketplaceAffiliateReportSearchSchemaType>();
  const { data, loading } = useMarketplaceAffiliateReportByDateQuery({
    variables: getValuableVariables({
      limit,
      endDate,
      startDate,
      campaignId,
      offset: getOffset(page, limit)
    })
  });

  const columns: ColumnDef<MarketplaceAffiliateReportByDateRowData>[] = [
    {
      accessorKey: 'date',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Date'),
      cell: ({ row }) => {
        const { className, date } = row.original;

        return className === 'total-row' ? (
          <TableCell.CampaignTotal
            title={
              <TableCell.Text value={formatAffiliatePeriod({ period: { endDate, startDate }, title: t('Total') })} />
            }
          />
        ) : className === 'average-row' ? (
          <TableCell.CampaignAverage title={t('Average')} />
        ) : (
          <TableCell.Date value={date} />
        );
      }
    },
    {
      accessorKey: 'clicks',
      header: t<string>('HeaderColumn.Click'),
      meta: { align: 'right', hidden: isReferralCampaign },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.clicks)} />
    },
    {
      accessorKey: 'conversions',
      header: t<string>('HeaderColumn.Order'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.conversions)} />
    },
    {
      accessorKey: 'cvr',
      header: t<string>('HeaderColumn.CVR'),
      meta: { align: 'right', hidden: isReferralCampaign },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.cvr, false)} unit="%" />
    },
    {
      accessorKey: 'sales',
      header: t<string>('HeaderColumn.Store Sales'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.sales || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'cost',
      header: t<string>('HeaderColumn.Cost'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.cost || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'influencerCost',
      header: t<string>('HeaderColumn.Influencer Cost'),
      meta: { align: 'right', hidden: !isAdminStaff },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.influencerCost || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'grossProfit',
      header: t<string>('HeaderColumn.Gross Profit'),
      meta: { align: 'right', hidden: !isAdminStaff },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.grossProfit || null, currency)} unit={currency} />
      )
    }
  ];

  const tableData: MarketplaceAffiliateReportByDateRowData[] = [
    { className: 'total-row', stats: summaryData?.summary },
    { className: 'average-row', stats: summaryData?.average },
    ...(data?.marketplaceAffiliateReportByDate?.map<MarketplaceAffiliateReportByDateRowData>(({ date, ...stats }) => ({
      date,
      stats
    })) || [])
  ];

  return (
    <TableV2
      className="report-table"
      loading={loading}
      rowHeight="73px"
      data={tableData}
      columns={columns}
      pagination={{ totalRecords: data?.marketplaceAffiliateReportByDateCount?.totalNumber }}
      noTopBorderRadius
      stickyHeader
    />
  );
};
